import { JIRA_BASE_URL, KLARNA_WIKIBASE_BASE_URL } from '@/config'

/**
 * The regular expression pattern for a valid Jira issue reference.
 *
 * e.g. `"CBRMS-1234"`
 */
const JIRA_ISSUE_PATTERN = /^[a-zA-Z]+-[1-9][0-9]+$/

/**
 * Check if a value is a valid Jira issue reference.
 *
 * For example: `"CBRMS-1234"` or `"IRIS-5678"`.
 *
 * @param value The value to check.
 * @returns `true` if the value is a valid JIRA issue reference, `false` otherwise.
 */
export function isJiraIssueRef(value?: string): boolean {
  return value ? JIRA_ISSUE_PATTERN.test(value.trim()) : false
}

/**
 * Get the URL for an issue or ticket reference.
 *
 * Supports both Jira and Actionable Improvement (AIM) issue references.
 *
 * If `issueRef` matches an `[A-Z]+-[1-9][0-9]+` pattern (e.g. `"CBRMS-1234"`),
 * a Jira issue URL is returned. Otherwise, it's considered an Actionable Improvement (AIM)
 * reference and a Wikibase URL is returned.
 *
 * @param issueRef The issue or ticket reference.
 * @returns The URL for the issue or ticket.
 */
export function getIssueUrl(issueRef: string): string {
  return isJiraIssueRef(issueRef)
    ? `${JIRA_BASE_URL}/browse/${encodeURIComponent(issueRef)}`
    : `${KLARNA_WIKIBASE_BASE_URL}/Item:${encodeURIComponent(issueRef)}`
}

/**
 * Normalize an issue or ticket reference.
 *
 * This converts the value to uppercase, removes all except alphanumeric
 * characters and dashes, replaces consecutive dashes with a single dash,
 * and removes any leading and trailing dashes.
 *
 * @param value The issue ref to normalize.
 * @returns The issue ref after all transformations are applied.
 */
export function normalizeIssueRef(value: string) {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9-]/g, '') // Remove all except capital letters, digits and dashes
    .replace(/-+/g, '-') // Replace consecutive dashes with a single dash
    .replace(/^-+|-+$/g, '') // Remove leading or trailing dashes, if any
}
